<template>
    <div class="autocomplete-group">
        <rkm-form-input
            :append="append"
            :disabled="disabled"
            :label="label"
            :placeholder="placeholder"
            :prepend="prepend"
            :validations="validations"
            @blur="handleBlur"
            @keydown="handleKeyDown"
            @update="handleUpdate"
            v-model="localValue"
            v-rkm-alpha="alpha"
            v-rkm-trim="trim"
            v-rkm-uppercase="uppercase"
        >
            <template
                v-if="!prepend && $slots.prepend"
                slot="prepend"
            >
                <slot name="prepend" />
            </template>

            <template
                v-if="!append && $slots.append"
                slot="append"
            >
                <slot name="append" />
            </template>
        </rkm-form-input>

        <ul
            v-if="searching && localValue.length >= searchTreshold"
            class="autocomplete-items"
        >
            <li
                v-if="busy"
                class="autocomplete-message"
            >
                <div class="text-center">
                    <rkm-icon icon="circle-notch" loading /> carregando...
                </div>
            </li>

            <template v-else>
                <template v-if="items.length > 0">
                    <li
                        v-for="(item, index) in items"
                        :key="index"
                        :class="{ 'autocomplete-item-active': index === activeItemIndex }"
                        @click="handleSelectItem(item)"
                        @mouseenter="handleMouseEnterItem(index)"
                        @mouseleave="handleMouseLeaveItem"
                        class="autocomplete-item"
                    >
                        {{ textField instanceof Object ? textField(item) : item[textField] }}
                    </li>
                </template>

                <li
                    v-else
                    class="autocomplete-message"
                >
                    Nada encontrado
                </li>
            </template>
        </ul>
    </div>
</template>

<script>
export default {
    data: () => ({
        activeItemIndex: 0,
        bouncer: null,
        busy: false,
        items: [],
        localValue: null,
        mouseOverItem: false,
        searching: false,
    }),
    methods: {
        handleBlur() {
            if (!this.value && !this.mouseOverItem) {
                this.activeItemIndex = 0
                this.busy = false
                this.items = []
                this.localValue = null
                this.searching = false
            }
        },
        handleKeyDown(event) {
            switch (event.key) {
                case 'Escape':
                    break
                case 'ArrowDown':
                    if (this.activeItemIndex < this.items.length - 1) {
                        this.activeItemIndex++
                    }
                    break
                case 'ArrowUp':
                    if (this.activeItemIndex > 0) {
                        this.activeItemIndex--
                    }
                    break
                case 'Enter':
                    if (this.items.length > 0) {
                        this.handleSelectItem(this.items[this.activeItemIndex])
                    }
                    break
            }
        },
        handleMouseEnterItem(index) {
            this.activeItemIndex = index
            this.mouseOverItem = true
        },
        handleMouseLeaveItem() {
            this.mouseOverItem = false
        },
        handleSelectItem(item) {
            this.localValue = this.textField instanceof Object ? this.textField(item) : item[this.textField]
            
            this.$emit('input', item[this.valueField])
            this.$emit('selected-item', item)

            this.activeItemIndex = 0
            this.items = []
            this.mouseOverItem = false
            this.searching = false
        },
        handleUpdate(value) {
            clearTimeout(this.bouncer)
            
            this.$emit('input', null)
            this.$emit('selected-item', null)

            this.searching = true

            if (value.trim().length >= parseInt(this.searchTreshold)) {
                this.bouncer = setTimeout(() => {
                    value = value.trim()

                    this.busy = true

                    const params = {
                        columns: this.fields.map(map => ({
                            data: map.key,
                            name: map.name,
                            searchable: true,
                        })),
                        draw: value.length,
                        length: 5,
                        search: {value: value},
                        start: '0',
                    }

                    this.repository.autocomplete({...params, ...this.requestParams})
                        .then(response => this.items = response.data.data)
                        .finally(() => this.busy = false)
                }, this.debounce)
            } else {
                this.searching = false
            }
        },
    },
    props: {
        alpha: {type: Boolean},
        append: {type: String},
        debounce: {type: Number, default: 300},
        disabled: {type: Boolean},
        fields: {type: Array, required: true},
        label: {type: String},
        placeholder: {type: String, default: 'Digite para buscar...'},
        prepend: {type: String},
        repository: {type: Object, required: true},
        requestParams: {type: Object},
        searchTreshold: {type: Number, default: 2},
        selectedText: {type: String},
        textField: {type: [Function, String], default: 'nome'},
        trim: {type: Boolean},
        uppercase: {type: Boolean},
        validations: {type: Object},
        value: {type: [Number, String]},
        valueField: {type: String, default: 'id'},
    },
    watch: {
        selectedText(value) {
            this.localValue = value
        },
        value(value) {
            if (!this.searching && !value) {
                this.localValue = null
            }
        },
    },
}
</script>

<style scoped>
.autocomplete-group {
    position: relative;
}
.autocomplete-items {
    background: #FFFF;
    border-top: 1px solid #eaeaea;
    border: 1px solid #ccd2d7;
    left: 0;
    max-height: 200px;
    overflow: auto;
    padding: 0.25rem;
    position: absolute;
    top: 65px;
    width: 100%;
    z-index: 9999;
}
.autocomplete-items::-webkit-scrollbar {
    background: none;
    background: transparent;
    width: 5px;
}
.autocomplete-items::-webkit-scrollbar-track {
    background: none;
    box-shadow: none;
}
.autocomplete-items::-webkit-scrollbar-thumb {
    background: #ccd2d7;
    border-radius: 10px;
}
.autocomplete-message {
    font-style: italic;
    padding: 0.25rem;
    text-align: center;
}
.autocomplete-item {
    border-radius: 3px;
    padding: 0.5rem;
}
.autocomplete-item-active {
    background: #007bff;
    color: #FFF;
    cursor: pointer;
}
.autocomplete-item[disabled] {
    cursor: not-allowed;
    opacity: 0.5;
}
.input-group > .autocomplete-group {
    -ms-flex: 1 1 auto;
    -webkit-box-flex: 1;
    flex: 1 1 auto;
    margin-bottom: 0;
    position: relative;
    width: 1%;
}
.input-group > .autocomplete-group:not(:last-child) > .form-control {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
}
.input-group > .autocomplete-group:not(:first-child) > .form-control {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
}
</style>